import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirestore, doc, updateDoc, collection, query, where, getDocs, addDoc, getDoc, arrayUnion } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../firebase';
import NaicsSearch from '../loanForm/NaicsSearch';
import CurrencyInput from '../loanForm/CurrencyInput';
import CapTable from '../loanForm/CapTable';
import StateSelect from '../loanForm/StateSelect';
import SelectQuestion from '../loanForm/SelectQuestion';
import FinancialInfo from '../loanForm/financialinfo';
import LoanStructure from '../loanForm/LoanStructure';
import RealEstateAppraisalInput from '../loanForm/RealEstateAppraisalInput';
import BusinessSummary from '../loanForm/BusinessSummary';
import { BuildingOfficeIcon, UsersIcon, BanknotesIcon, HomeIcon } from '@heroicons/react/24/outline';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const LoanForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [applicationDoc, setApplicationDoc] = useState(null); // The main loanApplication document
  const [businessIndex, setBusinessIndex] = useState(null); // Index of business being edited in the array
  const navigate = useNavigate();
  const location = useLocation();
  const isNewBusiness = location.state?.isNewBusiness || false;
  const editBusinessIndex = location.state?.editBusinessIndex ?? null; // Index in businesses array

  const formFields = [
    { name: 'businessName', type: 'input', label: 'What is the name of the business you are acquiring?', component: 'input', required: true },
    { name: 'loanSize', type: 'currency', label: 'What is the total purchase price for the business?', component: 'currency', required: true },
    { name: 'purchaseType', type: 'select', label: 'What type of purchase is it?', component: 'select', options: ['Stock', 'Asset', 'Unsure'], required: true },
    { name: 'businessState', type: 'select', label: 'What state is the business in?', component: 'stateSelect', required: true },
    { name: 'businessAge', type: 'select', label: 'How long has the company been in business?', component: 'select', options: ['Startup', '0-2 Years', '2+ Years'], required: true },
    { name: 'capTable', type: 'capTable', label: 'Cap Table', component: 'capTable', required: true },
    { name: 'realEstateAppraisal', type: 'currency', label: 'If there is real estate in the deal, what is its appraisal?', component: 'realEstateAppraisal', required: false },
    { name: 'loanStructure', type: 'loanStructure', label: 'Expected Loan Structure', component: 'loanStructure', required: true },
    { name: 'businessSummary', type: 'businessSummary', label: 'Can you provide a short summary of the business you are buying?', component: 'BusinessSummary', required: true },
    { name: 'additionalNotes', type: 'textarea', label: 'Additional Notes', component: 'textarea', required: false },
  ];

  // Business-specific fields that go in the businesses array
  const businessFields = ['businessName', 'loanSize', 'purchaseType', 'businessState', 'businessAge',
    'capTable', 'realEstateAppraisal', 'loanStructure', 'businessSummary', 'additionalNotes',
    'industry', 'status', 'loanapplicationstatus', 'currentStep'];

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchFormData = async () => {
      if (currentUser) {
        try {
          // Find the user's loanApplication document
          const formQuery = query(collection(db, 'loanApplications'), where('userId', '==', currentUser.uid));
          const querySnapshot = await getDocs(formQuery);

          if (querySnapshot.empty) {
            // No document exists - start fresh
            setFormData({ loanPurpose: 'Acquisition' });
            setApplicationDoc(null);
            setBusinessIndex(null);
            setCurrentStep(0);
            return;
          }

          const docSnap = querySnapshot.docs[0];
          const docData = docSnap.data();
          setApplicationDoc({ id: docSnap.id, ref: docSnap.ref });

          // If creating a new business, start with fresh business data
          if (isNewBusiness) {
            setFormData({ loanPurpose: 'Acquisition' });
            setBusinessIndex(null); // Will be set when saving
            setCurrentStep(0);
            return;
          }

          // If editing a specific business by index
          if (editBusinessIndex !== null && docData.businesses && docData.businesses[editBusinessIndex]) {
            const businessData = docData.businesses[editBusinessIndex];
            setFormData(businessData);
            setBusinessIndex(editBusinessIndex);
            setCurrentStep(Math.min(businessData.currentStep || 0, 2));
            return;
          }

          // Default: Check for old format (business data at root level) or load first business
          if (docData.businesses && docData.businesses.length > 0) {
            // New format: load first business
            const businessData = docData.businesses[0];
            setFormData(businessData);
            setBusinessIndex(0);
            setCurrentStep(Math.min(businessData.currentStep || 0, 2));
          } else if (docData.businessName || docData.loanSize) {
            // Old format: business data at root level - treat as first business
            // Extract business fields from root
            const businessData = {};
            businessFields.forEach(field => {
              if (docData[field] !== undefined) {
                businessData[field] = docData[field];
              }
            });
            setFormData(businessData);
            setBusinessIndex(-1); // Special flag for legacy data at root
            setCurrentStep(Math.min(docData.currentStep || 0, 2));
          } else {
            // No business data yet
            setFormData({ loanPurpose: 'Acquisition' });
            setBusinessIndex(null);
            setCurrentStep(0);
          }
        } catch (error) {
          console.error('Error fetching form data:', error);
        }
      }
    };

    fetchFormData();
  }, [currentUser, isNewBusiness, editBusinessIndex]);

  // Page-based wizard (0 = Business Info, 1 = Ownership + Structure, 2 = Final Details)
  const TOTAL_PAGES = 3;
  const validatePage = (pageIdx) => {
    if (pageIdx === 0) {
      const reqs = [
        { name: 'businessName', label: 'Business Name' },
        { name: 'loanSize', label: 'Purchase Price' },
        { name: 'purchaseType', label: 'Purchase Type' },
        { name: 'businessState', label: 'State' },
        { name: 'businessAge', label: 'Time in Business' },
        { name: 'businessSummary', label: 'Business Description' },
      ];
      for (const r of reqs) {
        if (!formData[r.name]) { alert(`Please fill in: ${r.label}`); return false; }
      }
      return true;
    }
    if (pageIdx === 1) {
      const total = formData.capTable?.reduce((sum, o) => sum + (parseFloat(o.percentage) || 0), 0);
      if (!formData.capTable || formData.capTable.length === 0 || total !== 100) {
        alert('Cap Table total must equal 100%');
        return false;
      }
      return true;
    }
    return true;
  };

  const handleNext = async () => {
    if (!validatePage(currentStep)) return;
    try {
      if (currentUser) {
        // Ensure businessId exists for new businesses
        const businessId = formData.businessId || `biz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const updatedBusinessData = {
          ...formData,
          businessId, // Unique identifier for this business
          currentStep: currentStep + 1,
          lastUpdated: new Date(),
        };

        if (applicationDoc) {
          // Get current document data
          const docSnap = await getDoc(applicationDoc.ref);
          const docData = docSnap.data();
          let businesses = docData.businesses || [];

          if (businessIndex === -1) {
            // Legacy data at root - migrate to businesses array
            // Keep root data for backwards compatibility, but also add to businesses array
            businesses = [updatedBusinessData];
            await updateDoc(applicationDoc.ref, {
              businesses,
              // Also update root level for backwards compatibility
              ...updatedBusinessData
            });
            setBusinessIndex(0);
          } else if (businessIndex !== null && businessIndex >= 0) {
            // Update existing business in array - preserve existing businessId
            updatedBusinessData.businessId = businesses[businessIndex]?.businessId || businessId;
            businesses[businessIndex] = updatedBusinessData;
            await updateDoc(applicationDoc.ref, { businesses });
          } else {
            // Adding new business to array — migrate legacy root data first if needed
            if (businesses.length === 0 && (docData.businessName || docData.loanSize)) {
              const legacyBusiness = {
                businessId: docData.businessId || `biz_legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                lastUpdated: new Date(),
              };
              businessFields.forEach(f => {
                if (docData[f] !== undefined) legacyBusiness[f] = docData[f];
              });
              businesses = [legacyBusiness, updatedBusinessData];
              setBusinessIndex(1);
            } else {
              businesses.push(updatedBusinessData);
              setBusinessIndex(businesses.length - 1);
            }
            await updateDoc(applicationDoc.ref, { businesses });
          }
        } else {
          // Create new document with businesses array
          const newDocRef = await addDoc(collection(db, 'loanApplications'), {
            userId: currentUser.uid,
            createdAt: new Date(),
            businesses: [updatedBusinessData],
            // Also store at root level for backwards compatibility with old code
            ...updatedBusinessData,
            loanapplicationstatus: 'in progress',
          });
          setApplicationDoc({ id: newDocRef.id, ref: newDocRef });
          setBusinessIndex(0);
        }
      }

      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error saving form data to Firestore:', error);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleExit = async () => {
    if (currentUser && applicationDoc && (businessIndex !== null || formData.businessName)) {
      try {
        // Ensure businessId exists
        const businessId = formData.businessId || `biz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const updatedBusinessData = {
          ...formData,
          businessId,
          currentStep,
          lastUpdated: new Date(),
        };

        const docSnap = await getDoc(applicationDoc.ref);
        const docData = docSnap.data();
        let businesses = docData.businesses || [];

        if (businessIndex === -1) {
          // Legacy - update both root and array
          businesses = [updatedBusinessData];
          await updateDoc(applicationDoc.ref, {
            businesses,
            ...updatedBusinessData
          });
        } else if (businessIndex !== null && businessIndex >= 0) {
          // Preserve existing businessId
          updatedBusinessData.businessId = businesses[businessIndex]?.businessId || businessId;
          businesses[businessIndex] = updatedBusinessData;
          await updateDoc(applicationDoc.ref, { businesses });
        } else if (formData.businessName) {
          // New business being added — migrate legacy root data first if needed
          if (businesses.length === 0 && (docData.businessName || docData.loanSize)) {
            const legacyBusiness = {
              businessId: docData.businessId || `biz_legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              lastUpdated: new Date(),
            };
            businessFields.forEach(f => {
              if (docData[f] !== undefined) legacyBusiness[f] = docData[f];
            });
            businesses = [legacyBusiness, updatedBusinessData];
          } else {
            businesses.push(updatedBusinessData);
          }
          await updateDoc(applicationDoc.ref, { businesses });
        }
      } catch (error) {
        console.error('Error saving form data to Firestore:', error);
      }
    }
    navigate('/portal');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      // Ensure businessId exists
      const businessId = formData.businessId || `biz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const validBusinessData = { loanapplicationstatus: 'submitted', lastUpdated: new Date(), businessId };

      // Filter out undefined values
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined) {
          validBusinessData[key] = value;
        }
      });

      if (applicationDoc) {
        const docSnap = await getDoc(applicationDoc.ref);
        const docData = docSnap.data();
        let businesses = docData.businesses || [];

        if (businessIndex === -1) {
          // Legacy - update both
          businesses = [validBusinessData];
          await updateDoc(applicationDoc.ref, {
            businesses,
            ...validBusinessData,
            lastUserUpdate: new Date(),
          });
        } else if (businessIndex !== null && businessIndex >= 0) {
          // Preserve existing businessId
          validBusinessData.businessId = businesses[businessIndex]?.businessId || businessId;
          businesses[businessIndex] = validBusinessData;
          await updateDoc(applicationDoc.ref, { businesses, lastUserUpdate: new Date() });
        } else {
          // New business — migrate legacy root data into businesses[] first so it isn't orphaned
          if (businesses.length === 0 && (docData.businessName || docData.loanSize)) {
            const legacyBusiness = {
              businessId: docData.businessId || `biz_legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              lastUpdated: new Date(),
            };
            businessFields.forEach(f => {
              if (docData[f] !== undefined) legacyBusiness[f] = docData[f];
            });
            businesses = [legacyBusiness, validBusinessData];
          } else {
            businesses.push(validBusinessData);
          }
          await updateDoc(applicationDoc.ref, { businesses, lastUserUpdate: new Date() });
        }
      }

      navigate('/portal');
    } catch (error) {
      console.error('Error submitting form data to Firestore:', error);
    }
  };

  const handleChange = (event) => {
    let name, value;
    if (event.target && event.target.name) {
      ({ name, value } = event.target);
    } else if (event.name !== undefined) {
      ({ name, value } = event);
    } else {
      console.error('Unexpected event structure:', event);
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCapTableChange = (value, pg) => {
    setFormData((prevData) => ({
      ...prevData,
      capTable: value,
      pg,
    }));
  };

  const renderField = (field) => {
    const currentField = field;
    switch (currentField.component) {
      case 'input':
        return (
          <>
            <label htmlFor={currentField.name} className="block mb-2 font-semibold text-gray-700">
              {currentField.label}
            </label>
            <input
              type="text"
              id={currentField.name}
              name={currentField.name}
              value={formData[currentField.name] || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 text-gray-700 border border-blue-900 rounded-lg focus:outline-none focus:border-blue-500"
              required={currentField.required}
            />
          </>
        );
      case 'select':
        return (
          <SelectQuestion
            name={currentField.name}
            label={currentField.label}
            options={currentField.options}
            value={formData[currentField.name] || ''}
            onChange={handleChange}
          />
        );
      case 'currency':
        return (
          <CurrencyInput
            name={currentField.name}
            label={currentField.label}
            value={formData[currentField.name] || ''}
            onChange={handleChange}
          />
        );
      case 'stateSelect':
        return (
          <StateSelect
            name={currentField.name}
            label={currentField.label}
            value={formData[currentField.name] || ''}
            onChange={handleChange}
          />
        );
      case 'naicsSearch':
        return (
          <NaicsSearch
            name={currentField.name}
            label={currentField.label}
            value={formData[currentField.name] || ''}
            onChange={handleChange}
          />
        );
      case 'capTable':
        return (
          <CapTable
            name={currentField.name}
            label={currentField.label}
            value={formData[currentField.name] || []}
            onChange={handleCapTableChange}
          />
        );
      case 'realEstateAppraisal':
        return (
          <RealEstateAppraisalInput
            name={currentField.name}
            label={currentField.label}
            value={formData[currentField.name] || ''}
            onChange={handleChange}
          />
        );
      case 'loanStructure':
        return (
          <LoanStructure
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 'financialInfo':
        return (
          <FinancialInfo
            formData={formData}
            handleChange={handleChange}
          />
        );
      case 'BusinessSummary':
        return (
          <BusinessSummary
            name={currentField.name}
            label={currentField.label}
            value={formData[currentField.name] || ''}
            onChange={(value) => handleChange({ name: currentField.name, value })}
          />
        );
      case 'textarea':
        return (
          <>
            <label htmlFor={currentField.name} className="block mb-2 font-semibold text-gray-700">
              {currentField.label}
            </label>
            <textarea
              id={currentField.name}
              name={currentField.name}
              value={formData[currentField.name] || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 text-gray-700 border border-blue-900 rounded-lg focus:outline-none focus:border-blue-500"
              required={currentField.required}
            ></textarea>
          </>
        );
      default:
        return null;
    }
  };

  // Validate all required fields before submit
  const validateAll = () => {
    for (const field of formFields) {
      if (!field.required) continue;
      if (field.component === 'capTable') {
        const total = formData.capTable?.reduce((sum, owner) => sum + (parseFloat(owner.percentage) || 0), 0);
        if (!formData.capTable || formData.capTable.length === 0 || total !== 100) {
          alert('Please fill in the Cap Table — total ownership must equal 100%.');
          return false;
        }
        continue;
      }
      if (!formData[field.name]) {
        alert(`Please fill in: ${field.label}`);
        return false;
      }
    }
    return true;
  };

  const handleFinalSubmit = async (event) => {
    event.preventDefault();
    if (!validateAll()) return;
    handleSubmit(event);
  };

  const canSubmit = formData.businessName && formData.loanSize && formData.purchaseType
    && formData.businessState && formData.businessAge && formData.businessSummary
    && (formData.capTable?.reduce((s, o) => s + (parseFloat(o.percentage) || 0), 0) === 100);

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-indigo-100 mb-3">
            <BuildingOfficeIcon className="h-7 w-7 text-indigo-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{isNewBusiness ? 'Add New Business' : 'Loan Application'}</h1>
          <p className="text-gray-500 mt-1 text-sm">Tell us about the business you're acquiring.</p>
          <button
            type="button"
            onClick={handleExit}
            className="mt-3 px-4 py-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            ← Save &amp; Exit to Portal
          </button>
        </div>

        <form onSubmit={handleFinalSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-6">
          {/* Business Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" />
              Business Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2 md:col-span-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                <input type="text" value={formData.businessName || ''}
                  onChange={(e) => handleChange({ name: 'businessName', value: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                  <input type="number" value={formData.loanSize || ''}
                    onChange={(e) => handleChange({ name: 'loanSize', value: e.target.value })}
                    placeholder="2000000"
                    className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Type *</label>
                <select value={formData.purchaseType || ''}
                  onChange={(e) => handleChange({ name: 'purchaseType', value: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="">Select…</option>
                  <option value="Stock">Stock</option>
                  <option value="Asset">Asset</option>
                  <option value="Unsure">Unsure</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                <select value={formData.businessState || ''}
                  onChange={(e) => handleChange({ name: 'businessState', value: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="">Select…</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time in Business *</label>
                <select value={formData.businessAge || ''}
                  onChange={(e) => handleChange({ name: 'businessAge', value: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="">Select…</option>
                  <option value="Startup">Startup</option>
                  <option value="0-2 Years">0-2 Years</option>
                  <option value="2+ Years">2+ Years</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Description *</label>
              <textarea value={formData.businessSummary || ''}
                onChange={(e) => handleChange({ name: 'businessSummary', value: e.target.value })}
                rows={4}
                placeholder="What does the business do? Years in operation, location, key customers, what's driving the sale."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
          </div>

          {/* Ownership + Loan Structure (side by side) */}
          <div className="border-t pt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
                <UsersIcon className="h-5 w-5 text-indigo-600" />
                Ownership
              </h3>
              <p className="text-xs text-gray-500 mb-3">Add owners and ownership percentages — total must equal 100%.</p>
              <CapTable name="capTable" label="" value={formData.capTable || []} onChange={handleCapTableChange} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
                <BanknotesIcon className="h-5 w-5 text-indigo-600" />
                Loan Structure
              </h3>
              <p className="text-xs text-gray-500 mb-3">How is the purchase being financed?</p>
              <LoanStructure formData={formData} setFormData={setFormData} />
            </div>
          </div>

          {/* Deal Options */}
          <div className="border-t pt-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Deal Options</h3>
            <p className="text-xs text-gray-500 mb-3">Anything that applies to this deal — helps us request the right docs.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className={`rounded-lg border md:col-span-2 ${formData.realEstateIncluded ? 'border-indigo-200 bg-indigo-50/40' : 'border-gray-200'}`}>
                <label className="flex items-start gap-2 cursor-pointer p-3 hover:bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={!!formData.realEstateIncluded}
                    onChange={(e) => handleChange({ name: 'realEstateIncluded', value: e.target.checked })}
                    className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Real estate is part of the deal</span>
                    <span className="block text-xs text-gray-500 mt-0.5">We'll ask for an appraisal alongside your business docs.</span>
                  </span>
                </label>
                {formData.realEstateIncluded && (
                  <div className="px-3 pb-3 pl-9 max-w-sm">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Estimated real estate value</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                      <input
                        type="number"
                        value={formData.realEstateAppraisal || ''}
                        onChange={(e) => handleChange({ name: 'realEstateAppraisal', value: e.target.value })}
                        placeholder="e.g. 500000"
                        className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={!!formData.licenseNeeded}
                  onChange={(e) => handleChange({ name: 'licenseNeeded', value: e.target.checked })}
                  className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Business needs a license to operate</span>
                  <span className="block text-xs text-gray-500 mt-0.5">E.g. liquor, contractor, professional services.</span>
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={!!formData.hasMultipleLLCs}
                  onChange={(e) => handleChange({ name: 'hasMultipleLLCs', value: e.target.checked })}
                  className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Multiple LLCs / entities being acquired</span>
                  <span className="block text-xs text-gray-500 mt-0.5">We'll request tax returns for each entity.</span>
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={!!formData.hasOtherAddbacks}
                  onChange={(e) => handleChange({ name: 'hasOtherAddbacks', value: e.target.checked })}
                  className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Significant addbacks to expect</span>
                  <span className="block text-xs text-gray-500 mt-0.5">We'll need an addbacks schedule with documentation.</span>
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={!!formData.topCustomerConcentration}
                  onChange={(e) => handleChange({ name: 'topCustomerConcentration', value: e.target.checked })}
                  className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  <span className="font-medium">Major customer concentration risk</span>
                  <span className="block text-xs text-gray-500 mt-0.5">One or a few customers drive most of the revenue.</span>
                </span>
              </label>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="border-t pt-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes <span className="text-xs font-normal text-gray-500">(optional)</span></label>
            <textarea value={formData.additionalNotes || ''}
              onChange={(e) => handleChange({ name: 'additionalNotes', value: e.target.value })}
              rows={3}
              placeholder="Anything else we should know."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-3 px-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit Application
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoanForm;
